import React, { useEffect, useState } from 'react';
import { CalendarRange, Check, ChevronLeft, ChevronRight, Lock, RotateCcw } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  MONTH_NAMES,
  fyWindow,
  hydrateFinancialYear,
  saveFinancialYear,
  useFinancialYear,
} from '../../lib/financialYear';

function formatYmd(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? ymd
    : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Tenant-wide financial year: start month + the year reports and dashboards open on.
 * Saved on the server so every user, ledger and device sees the same year.
 */
const FinancialYearSettings: React.FC = () => {
  const fy = useFinancialYear();
  const [startMonth, setStartMonth] = useState(fy.startMonth);
  const [startYear, setStartYear] = useState(fy.selected.startYear);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    hydrateFinancialYear().catch(() => {});
  }, []);

  useEffect(() => {
    setStartMonth(fy.startMonth);
    setStartYear(fy.selected.startYear);
  }, [fy.startMonth, fy.selected.startYear]);

  useEffect(() => {
    if (!saved) return;
    const timer = window.setTimeout(() => setSaved(false), 2500);
    return () => window.clearTimeout(timer);
  }, [saved]);

  const preview = fyWindow(startYear, startMonth);
  const currentForMonth = fyWindow(
    new Date().getMonth() + 1 >= startMonth ? new Date().getFullYear() : new Date().getFullYear() - 1,
    startMonth
  );
  const isCurrent = preview.startYear === currentForMonth.startYear;
  const maxYear = currentForMonth.startYear + 1;
  const dirty = startMonth !== fy.startMonth || startYear !== fy.selected.startYear;
  const readOnly = !fy.canManage;

  const save = async (followCurrent = false) => {
    setSaving(true);
    try {
      await saveFinancialYear({
        startMonth,
        selectedStartYear: followCurrent || isCurrent ? null : startYear,
      });
      setSaved(true);
      toast.success('Financial year saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save the financial year');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-2 text-zinc-900 dark:text-zinc-100">
        <CalendarRange className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700 dark:text-emerald-400" aria-hidden />
        <p className="text-sm text-zinc-600 dark:text-zinc-400">
          Reports, the accounting dashboard and the ERP suite open on this year when you pick{' '}
          <span className="font-semibold text-zinc-800 dark:text-zinc-200">Financial year</span> in a date filter.
          Changing it never edits transactions, EMIs or balances.
        </p>
      </div>

      {readOnly && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300">
          <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
          View only. An Owner or an admin with Settings write access can change the financial year.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="app-label" htmlFor="fy-start-month">
            Year starts in
          </label>
          <select
            id="fy-start-month"
            className="app-field"
            value={startMonth}
            disabled={readOnly || saving}
            onChange={(e) => setStartMonth(Number(e.target.value))}
          >
            {MONTH_NAMES.map((name, i) => (
              <option key={name} value={i + 1}>
                {name}
                {i + 1 === 4 ? ' (Indian FY default)' : ''}
              </option>
            ))}
          </select>
          <p className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-400">Most Indian businesses use 1 April – 31 March.</p>
        </div>

        <div>
          <span className="app-label">Selected year</span>
          <div className="flex h-10 items-stretch overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
            <button
              type="button"
              className="flex w-10 items-center justify-center text-zinc-600 hover:bg-zinc-50 disabled:opacity-40 dark:text-zinc-300 dark:hover:bg-zinc-800"
              onClick={() => setStartYear((y) => y - 1)}
              disabled={readOnly || saving || startYear <= 1990}
              aria-label="Previous financial year"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex flex-1 items-center justify-center gap-2 border-x border-zinc-200 text-sm font-semibold text-zinc-900 dark:border-zinc-700 dark:text-zinc-100">
              {preview.label}
              {isCurrent && (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
                  Current
                </span>
              )}
            </div>
            <button
              type="button"
              className="flex w-10 items-center justify-center text-zinc-600 hover:bg-zinc-50 disabled:opacity-40 dark:text-zinc-300 dark:hover:bg-zinc-800"
              onClick={() => setStartYear((y) => y + 1)}
              disabled={readOnly || saving || startYear >= maxYear}
              aria-label="Next financial year"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-400">
            {formatYmd(preview.startYmd)} – {formatYmd(preview.endYmd)}
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-200 bg-zinc-50/80 px-4 py-3 text-xs text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-400">
        {fy.followsCurrent ? (
          <>Saved: follows the current year automatically ({fy.current.label}) and rolls over each {MONTH_NAMES[fy.startMonth - 1]}.</>
        ) : (
          <>
            Saved: pinned to <span className="font-semibold text-zinc-800 dark:text-zinc-200">{fy.selected.label}</span>. The current
            year is {fy.current.label}.
          </>
        )}
        {fy.updatedAt && (
          <span className="block pt-1 text-zinc-400">
            Last changed {new Date(fy.updatedAt).toLocaleString('en-IN')}
            {fy.updatedBy ? ` by ${fy.updatedBy}` : ''}
          </span>
        )}
      </div>

      {!readOnly && (
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-zinc-100 pt-4 dark:border-zinc-800">
          {saved && (
            <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 dark:text-emerald-400">
              <Check className="h-4 w-4" /> Saved
            </span>
          )}
          {!fy.followsCurrent && (
            <button type="button" className="app-btn-secondary inline-flex items-center gap-1.5" disabled={saving} onClick={() => save(true)}>
              <RotateCcw className="h-4 w-4" /> Follow current year
            </button>
          )}
          <button type="button" className="app-btn-primary" disabled={saving || !dirty} onClick={() => save(false)}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
};

export default FinancialYearSettings;
